// frontend/src/components/SEO/SEO.tsx
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  noIndex?: boolean;
}

const SITE_ORIGIN = "https://acceptingmd.ca";

function resolveCanonical(explicit?: string): string {
  if (explicit) return explicit;

  if (typeof window === "undefined") {
    return `${SITE_ORIGIN}/`;
  }

  const rawPath = window.location.pathname || "/";
  const pathname =
    rawPath.length > 1 && rawPath.endsWith("/") ? rawPath.slice(0, -1) : rawPath;

  if (pathname === "/" || pathname === "/public") {
    return `${SITE_ORIGIN}/`;
  }

  return `${SITE_ORIGIN}${pathname}`;
}

/**
 * SEO Component - Manages page-specific meta tags
 *
 * Usage:
 * <SEO
 *   title="Find a Family Doctor"
 *   description="Search for family doctors accepting new patients near you."
 * />
 *
 * The title will be appended with " | AcceptingMD"
 */
export default function SEO({ title, description, canonical, noIndex }: SEOProps) {
  const siteTitle = "AcceptingMD";
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const resolvedCanonical = resolveCanonical(canonical);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={resolvedCanonical} />

      {/* Twitter */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* Canonical URL */}
      <link rel="canonical" href={resolvedCanonical} />

      {/* No Index (for pages that shouldn't be in search results) */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
}
